import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { ArrowLeftIcon } from '@phosphor-icons/react/ArrowLeft';
import { ArrowRightIcon } from '@phosphor-icons/react/ArrowRight';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useCarousel } from './useCarousel.ts';

type CarouselProps = {
  children: ReactNode;
  maxWidth?: number | string;
};

export const Carousel = ({ children, maxWidth }: CarouselProps) => {
  const { trackRef, activeIndex, slides, hasMultipleSlides, scrollToSlide, handlePrevious, handleNext } =
    useCarousel(children);
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
        }}
      >
        <Box
          ref={trackRef}
          sx={{
            display: 'flex',
            width: '100%',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            scrollbarWidth: 'none',

            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {slides.map((slide, index) => (
            <Box
              key={index}
              sx={{
                flex: '0 0 100%',
                minWidth: '100%',
                scrollSnapAlign: 'start',
              }}
            >
              {slide}
            </Box>
          ))}
        </Box>

        {hasMultipleSlides && (
          <IconButton
            aria-label={t('Previous slide')}
            color="secondary"
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '-64px',
              transform: 'translateY(-50%)',
              zIndex: 1,
              width: 32,
              height: 32,
              borderRadius: 1,

              '& svg': {
                fontSize: 13,
              },
            }}
          >
            <ArrowLeftIcon />
          </IconButton>
        )}

        {hasMultipleSlides && (
          <IconButton
            aria-label={t('Next slide')}
            color="secondary"
            onClick={handleNext}
            sx={{
              position: 'absolute',
              top: '50%',
              right: '-64px',
              transform: 'translateY(-50%)',
              zIndex: 1,
              width: 32,
              height: 32,
              borderRadius: 1,

              '& svg': {
                fontSize: 13,
              },
            }}
          >
            <ArrowRightIcon />
          </IconButton>
        )}
      </Box>

      {hasMultipleSlides && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 5,
          }}
        >
          {slides.map((_, index) => (
            <Box
              key={index}
              component="button"
              aria-label={t('Go to slide {number}', { number: index + 1 })}
              aria-current={activeIndex === index}
              onClick={() => scrollToSlide(index)}
              sx={{
                width: 10,
                height: 10,
                p: 0,
                border: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor: activeIndex === index ? 'primary.main' : 'background.boxDarkest',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
